import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap, map, finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PKCEUtil } from '../utils/pkce.util';
import { SessionService } from './session.service';
import { UserService } from './user.service';

export interface AuthToken {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  created_at: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'oauth_token';
  private readonly CODE_VERIFIER_KEY = 'oauth_code_verifier';
  private readonly STATE_KEY = 'oauth_state';
  private readonly REDIRECT_PATH_KEY = 'oauth_redirect_path';

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private sessionService: SessionService,
    private userService: UserService
  ) {
    this.checkAuthStatus();
  }

  private setStorageItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('Storage access error:', error);
    }
  }

  private getStorageItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('Storage access error:', error);
      return null;
    }
  }

  private removeStorageItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Storage access error:', error);
    }
  }

  private getStoredToken(): AuthToken | null {
    const tokenStr = this.getStorageItem(this.TOKEN_KEY);
    return tokenStr ? JSON.parse(tokenStr) : null;
  }

  public get isLoggedIn(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  private checkAuthStatus(): void {
    const token = this.getStoredToken();
    this.isAuthenticatedSubject.next(!!token);
  }

  setRedirectPath(path: string): void {
    this.setStorageItem(this.REDIRECT_PATH_KEY, path);
  }

  getRedirectPath(): string | null {
    return this.getStorageItem(this.REDIRECT_PATH_KEY);
  }

  clearRedirectPath(): void {
    this.removeStorageItem(this.REDIRECT_PATH_KEY);
  }

  login(redirectPath?: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        if (redirectPath) {
          this.setRedirectPath(redirectPath);
        }

        // Check if we already have code verifier and state in storage
        let codeVerifier = this.getStorageItem(this.CODE_VERIFIER_KEY);
        let state = this.getStorageItem(this.STATE_KEY);

        // Generate new values only if they don't exist
        if (!codeVerifier) {
          codeVerifier = PKCEUtil.generateCodeVerifier();
          this.setStorageItem(this.CODE_VERIFIER_KEY, codeVerifier);
        }

        if (!state) {
          state = PKCEUtil.generateRandomString(32);
          this.setStorageItem(this.STATE_KEY, state);
        }

        const codeChallenge = await PKCEUtil.generateCodeChallenge(codeVerifier);

        const params = new URLSearchParams({
          response_type: 'code',
          client_id: environment.jira.clientId,
          redirect_uri: environment.jira.redirectUri,
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
          scope: environment.jira.scope,
          state: state
        });

        const authUrl = `${environment.jira.baseUrl}${environment.jira.authorizationEndpoint}?${params}`;
        window.location.href = authUrl;
        resolve();
      } catch (error) {
        console.error('Login error:', error);
        reject(error);
      }
    });
  }

  handleCallback(code: string, state: string): Observable<void> {
    const codeVerifier = this.getStorageItem(this.CODE_VERIFIER_KEY);
    const storedState = this.getStorageItem(this.STATE_KEY);

    if (!codeVerifier) {
      return throwError(() => new Error('No code verifier found'));
    }
    if (!storedState || state !== storedState) {
      return throwError(() => new Error('Invalid state parameter'));
    }

    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('client_id', environment.jira.clientId);
    body.set('client_secret', environment.jira.clientSecret);
    body.set('code_verifier', codeVerifier);
    body.set('code', code);
    body.set('redirect_uri', environment.jira.redirectUri);

    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('Accept', '*/*')
      .set('Cache-Control', 'no-cache');

    return this.http.post<AuthToken>(
      `${environment.jira.tokenEndpoint}`,
      body.toString(),
      { headers, withCredentials: true }
    ).pipe(
      tap(response => this.handleAuthenticationSuccess(response)),
      map(() => void 0),
      catchError(error => this.handleError(error)),
      finalize(() => {
        this.removeStorageItem(this.CODE_VERIFIER_KEY);
        this.removeStorageItem(this.STATE_KEY);
      })
    );
  }


  async logout(): Promise<void> {
    // Call backend to invalidate session
    await this.sessionService.logout();
    // Clear everything from localStorage to fully log the user out
    localStorage.clear();
    this.isAuthenticatedSubject.next(false);
    this.userService.clearUser();
  }

  getAccessToken(): string | null {
    const token = this.getStoredToken();
    return token?.access_token ?? null;
  }

  private handleAuthenticationSuccess(token: AuthToken): void {
    const tokenWithTimestamp: AuthToken = {
      ...token,
      created_at: token.created_at || Math.floor(Date.now() / 1000)
    };

    this.setStorageItem(this.TOKEN_KEY, JSON.stringify(tokenWithTimestamp));
    this.isAuthenticatedSubject.next(true);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      switch (error.status) {
        case 400:
          errorMessage = 'Invalid request';
          break;
        case 401:
          errorMessage = 'Unauthorized';
          break;
        case 403:
          errorMessage = 'Forbidden';
          break;
        case 404:
          errorMessage = 'Not found';
          break;
        default:
          errorMessage = `Server error: ${error.status}`;
      }
    }

    console.error('Auth error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
